import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import Select from 'react-select'; 
import AppBreadCrumb from '../../common/AppBreadCrumb';
import AppToaster from '../../AppToaster';
import { getPosProducts, createSalesInvoice, updateSalesInvoice, fetchSalesInvoice } from '../../../api/SalesInvoicesAPI';
import { getParties, createParty } from '../../../api/PartiesAPI';
import { getFranchiseeSettings } from '../../../api/FranchiseeSettingsAPI';
import { getBaseUrl, printPDF } from '../../../api/risapi'; 

import ProductGrid from './ProductGrid';
import CartTable from './CartTable';

const SalesInvoiceForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: invoiceIdParam } = useParams(); 
    
    const isEditMode = location.pathname.includes('/edit/');
    const isViewMode = location.pathname.includes('/view/');
    const isReadOnly = isViewMode; 

    const [products, setProducts] = useState([]);
    const [parties, setParties] = useState([]);
    const [defaultParty, setDefaultParty] = useState(null);
    const [settings, setSettings] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [cart, setCart] = useState([]);
    const [selectedParty, setSelectedParty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showCheckout, setShowCheckout] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [lastInvoiceId, setLastInvoiceId] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
    const [paymentMode, setPaymentMode] = useState('cash');

    const [showPartyModal, setShowPartyModal] = useState(false);
    const [newPartyName, setNewPartyName] = useState('');
    const [newPartyPhone, setNewPartyPhone] = useState('');
    const [newPartyGstin, setNewPartyGstin] = useState('');
    const [toaster, setToaster] = useState({});
    const [showToaster, setShowToaster] = useState(false);
    const searchInputRef = useRef(null);

    // --- NEW: Barcode Modal State ---
    const [showBarcodeQtyModal, setShowBarcodeQtyModal] = useState(false);
    const [scannedProduct, setScannedProduct] = useState(null);
    const [scannedQty, setScannedQty] = useState('');
    const barcodeQtyInputRef = useRef(null);

    const barcodeBuffer = useRef('');
    const barcodeTimeout = useRef(null);

    // Focus input automatically when barcode modal opens
    useEffect(() => {
        if (showBarcodeQtyModal && barcodeQtyInputRef.current) {
            setTimeout(() => {
                barcodeQtyInputRef.current.focus();
                barcodeQtyInputRef.current.select(); 
            }, 100);
        }
    }, [showBarcodeQtyModal]);

    // Global Keydown Listener for Hardware Scanners
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            const activeElement = document.activeElement;
            // Ignore if typing in a text field
            if (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.tagName) && activeElement.type !== 'radio' && activeElement.type !== 'checkbox') {
                return;
            }
            // Ignore if ANY modal is currently open (prevents scanning while checking out or confirming qty)
            if (document.querySelector('.modal.d-block') || document.querySelector('.swal2-container')) {
                return; 
            }

            if (e.key !== 'Enter') {
                barcodeBuffer.current += e.key;
                if (barcodeTimeout.current) clearTimeout(barcodeTimeout.current);
                barcodeTimeout.current = setTimeout(() => {
                    barcodeBuffer.current = '';
                }, 50); // 50ms threshold to identify rapid scanner input
            } else {
                if (barcodeBuffer.current.length > 3) { 
                    const scannedCode = barcodeBuffer.current;
                    barcodeBuffer.current = ''; 
                    processBarcodeScan(scannedCode);
                }
            }
        };

        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [products]); 

    const processBarcodeScan = (scannedCode) => {
        if (isReadOnly) return;
        const matchedProduct = products.find(p => p.barcode === scannedCode || p.code === scannedCode);

        if (!matchedProduct) {
            setToaster({ type: 'danger', heading: 'Error', message: `Barcode ${scannedCode} not found.` });
            setShowToaster(true);
            return;
        }

        // Open custom React modal instead of SweetAlert
        setScannedProduct(matchedProduct);
        setScannedQty(1); // Default quantity
        setShowBarcodeQtyModal(true);
    };

    const handleConfirmBarcodeQty = () => {
        const qtyToAdd = parseFloat(scannedQty);
        if (qtyToAdd > 0 && scannedProduct) {
            addToCartWithQty(scannedProduct, qtyToAdd);
            setShowBarcodeQtyModal(false);
            setScannedProduct(null);
            setScannedQty('');
        } else {
            setToaster({ type: 'warning', heading: 'Invalid', message: 'Please enter a valid quantity.' });
            setShowToaster(true);
        }
    };

    const addToCartWithQty = (product, newQty) => {
        const existingItem = cart.find(item => (item.product_id || item.id) === product.id);
        
        if (existingItem) {
            setCart(cart.map(item => (item.product_id || item.id) === product.id ? { ...item, qty: item.qty + newQty } : item));
        } else {
            let activeRate = product.rates?.find(r => parseInt(r.uom_id) === parseInt(product.base_uom_id));
            if (!activeRate && product.rates && product.rates.length > 0) activeRate = product.rates[0];
            activeRate = activeRate || {};

            const defaultUomId = activeRate.uom_id || product.base_uom_id;
            let defaultConvFactor = 1;
            if (parseInt(defaultUomId) !== parseInt(product.base_uom_id)) {
                const pu = product.product_units?.find(u => parseInt(u.uom_id) === parseInt(defaultUomId));
                if (pu) defaultConvFactor = parseFloat(pu.conversion_factor);
            }

            setCart(prevCart => [...prevCart, {
                id: product.id, product_id: product.id, name: product.name, qty: newQty,
                base_price: parseFloat(activeRate.base_price || product.current_price || 0),
                stock: parseFloat(product.current_stock || 0),
                uom_id: defaultUomId,
                conversion_factor: defaultConvFactor,
                allow_negative_stock: product.allow_negative_stock,
                rate_id: activeRate.id || null,
                adjustments: activeRate.adjustments || [],
                product_ref: product 
            }]);
        }
        
        setToaster({ type: 'success', heading: 'Added', message: `${newQty}x ${product.name} added to cart.` });
        setShowToaster(true);
    };

    // Advanced Math Calculation
    const calculateItemFinancials = (item) => {
        const qty = parseFloat(item.qty || item.sale_quantity || 1);
        const basePrice = parseFloat(item.base_price || item.sale_price || 0); 
        const baseAmount = qty * basePrice;

        let totalDeductions = 0;
        let totalAdditions = 0;
        const adjustments = item.adjustments || [];

        adjustments.filter(a => a.is_subtraction).forEach(adj => {
            let adjVal = adj.percentage ? (baseAmount * parseFloat(adj.percentage)) / 100 : parseFloat(adj.amount || 0);
            totalDeductions += adjVal;
        });

        const taxableAmount = baseAmount - totalDeductions;

        adjustments.filter(a => !a.is_subtraction).forEach(adj => {
            let adjVal = adj.percentage ? (taxableAmount * parseFloat(adj.percentage)) / 100 : parseFloat(adj.amount || 0);
            totalAdditions += adjVal;
        });

        const lineTotal = taxableAmount + totalAdditions;
        const effectiveUnitRate = qty > 0 ? lineTotal / qty : basePrice;

        return {
            qty, basePrice, baseAmount, totalDeductions, taxableAmount, totalAdditions,
            lineTotal: isNaN(lineTotal) ? baseAmount : lineTotal,
            effectiveUnitRate: isNaN(effectiveUnitRate) ? basePrice : effectiveUnitRate,
            adjustments
        };
    };

    // Load Init Data
    useEffect(() => {
        setLoading(true);
        Promise.all([
            new Promise(resolve => getFranchiseeSettings(resolve)),
            new Promise(resolve => getParties({ paginate: false }, resolve)),
            new Promise(resolve => getPosProducts(resolve))
        ]).then(([settingsRes, partyRes, prodRes]) => {
            
            let loadedSettings = null;
            let loadedParties = [];

            if (settingsRes && settingsRes.settings) {
                loadedSettings = settingsRes.settings;
                setSettings(loadedSettings);
            }

            if (partyRes && partyRes.parties && !partyRes.error) {
                loadedParties = partyRes.parties;
                setParties(loadedParties);
            }

            if (!invoiceIdParam && loadedParties.length > 0) {
                const config = loadedSettings?.setting || loadedSettings || {};
                let defaultSelected = false;

                if (config.default_party_id) {
                    const matchedDefaultParty = loadedParties.find(p => parseInt(p.id) === parseInt(config.default_party_id));
                    if (matchedDefaultParty) {
                        const defOpt = { value: matchedDefaultParty.id, label: matchedDefaultParty.name };
                        setDefaultParty(defOpt);
                        setSelectedParty(defOpt);
                        defaultSelected = true;
                    }
                }

                if (!defaultSelected) {
                    const walkIn = loadedParties.find(p => p.name.toLowerCase() === 'walk in' || p.name.toLowerCase() === 'walk-in');
                    if (walkIn) {
                        const walkInOpt = { value: walkIn.id, label: walkIn.name };
                        setDefaultParty(walkInOpt);
                        setSelectedParty(walkInOpt);
                    }
                }
            }

            if (prodRes) {
                const productList = Array.isArray(prodRes) ? prodRes : (prodRes.products || prodRes.data || []);
                setProducts(Array.isArray(productList) ? productList : []);
            }

            setLoading(false);
        }).catch(err => {
            setToaster({ type: 'danger', heading: 'Error', message: 'Failed to load initial data.' });
            setShowToaster(true); setLoading(false);
        });
    }, [invoiceIdParam]);

    // Load Existing Invoice
    useEffect(() => {
        if (invoiceIdParam && parties.length > 0 && products.length > 0) {
            setLoading(true);
            fetchSalesInvoice(invoiceIdParam, (res) => {
                setLoading(false);
                if (res.error || !res.sales_invoice) return AppToaster.error("Failed to load invoice details.");
                const inv = res.sales_invoice;

                if (inv.invoice_date) setInvoiceDate(inv.invoice_date.split('T')[0]);
                if (inv.party_id) {
                    const matchedParty = parties.find(p => p.id === inv.party_id);
                    if (matchedParty) setSelectedParty({ value: matchedParty.id, label: matchedParty.name });
                }

                if (inv.items && Array.isArray(inv.items)) {
                    const loadedCart = inv.items.map(item => {
                        const matchedProduct = products.find(p => p.id === item.product_id);
                        const baseP = item.rate?.base_price || item.sale_price || matchedProduct?.current_price || 0;
                        return {
                            id: item.product_id, product_id: item.product_id,
                            name: item.product?.name || matchedProduct?.name || 'Unknown',
                            qty: parseFloat(item.sale_quantity || 1),
                            base_price: parseFloat(baseP),
                            stock: matchedProduct ? parseFloat(matchedProduct.current_stock || 0) : 0,
                            uom_id: item.sale_uom_id || matchedProduct?.base_uom_id,
                            conversion_factor: parseFloat(item.conversion_factor || 1),
                            allow_negative_stock: item.product?.allow_negative_stock || matchedProduct?.allow_negative_stock,
                            rate_id: item.rate_id || null,
                            adjustments: item.adjustments || [],
                            product_ref: matchedProduct 
                        };
                    });
                    setCart(loadedCart);
                }
            });
        }
    }, [invoiceIdParam, parties, products]);

    // Click to add
    const addToCart = (product) => {
        if (isReadOnly) return;
        addToCartWithQty(product, 1);
    };

    const handleQtyChange = (rowKey, newQty) => {
        if (isReadOnly || parseFloat(newQty) < 1) return;
        setCart(cart.map(item => (item.product_id || item.id) === rowKey ? { ...item, qty: parseFloat(newQty) } : item));
    };

    const removeFromCart = (rowKey) => {
        if (isReadOnly) return;
        setCart(cart.filter(item => (item.product_id || item.id) !== rowKey));
    };

    const handleUomChange = (rowKey, newUomId) => {
        if (isReadOnly) return;
        setCart(cart.map(item => {
            if ((item.product_id || item.id) === rowKey) {
                const product = item.product_ref;
                if (!product) return item;

                let convFactor = 1;
                if (parseInt(newUomId) !== parseInt(product.base_uom_id)) {
                    const pu = product.product_units?.find(u => parseInt(u.uom_id) === parseInt(newUomId));
                    if (pu) convFactor = parseFloat(pu.conversion_factor);
                }

                const currentRate = product.rates?.find(r => r.id === item.rate_id);
                const currentRateTypeId = currentRate ? currentRate.rate_type_id : null;

                let newRate = product.rates?.find(r => parseInt(r.uom_id) === parseInt(newUomId) && r.rate_type_id === currentRateTypeId);
                if (!newRate) newRate = product.rates?.find(r => parseInt(r.uom_id) === parseInt(newUomId));

                return {
                    ...item,
                    uom_id: parseInt(newUomId),
                    conversion_factor: convFactor,
                    rate_id: newRate ? newRate.id : null,
                    base_price: newRate ? parseFloat(newRate.base_price || 0) : 0,
                    adjustments: newRate ? (newRate.adjustments || []) : []
                };
            }
            return item;
        }));
    };

    const handleRateChange = (rowKey, newRateId) => {
        if (isReadOnly) return;
        setCart(cart.map(item => {
            if ((item.product_id || item.id) === rowKey) {
                const product = item.product_ref;
                if (!product) return item;

                const newRate = product.rates?.find(r => parseInt(r.id) === parseInt(newRateId));
                if (newRate) {
                    return {
                        ...item,
                        rate_id: newRate.id,
                        base_price: parseFloat(newRate.base_price || 0),
                        adjustments: newRate.adjustments || []
                    };
                }
            }
            return item;
        }));
    };

    const grandTotal = useMemo(() => {
        return cart.reduce((total, item) => total + calculateItemFinancials(item).lineTotal, 0);
    }, [cart]);

    const handleCreateParty = () => {
        if (!newPartyName) return Swal.fire('Validation Error', 'Customer name is required', 'warning');
        createParty({ name: newPartyName, phone: newPartyPhone, gstin: newPartyGstin }, (res) => {
            if (res.error) return Swal.fire('Error', 'Failed to create customer', 'error');
            setParties([...parties, res.party]);
            setSelectedParty({ value: res.party.id, label: res.party.name });
            setShowPartyModal(false); setNewPartyName(''); setNewPartyPhone(''); setNewPartyGstin('');
            AppToaster.success('Customer created successfully');
        });
    };

   const submitInvoice = () => {
        if (!selectedParty) return Swal.fire('Error', 'Please select a customer.', 'error');
        
        let amountReceived = 0; let paymentModeHoaId = null;
        const config = settings?.setting || settings || {};

        if (paymentMode === 'cash') { amountReceived = grandTotal; paymentModeHoaId = config.default_cash_hoa_id; }
        else if (paymentMode === 'qr') { amountReceived = grandTotal; paymentModeHoaId = config.default_qr_hoa_id; }
        else if (paymentMode === 'card') { amountReceived = grandTotal; paymentModeHoaId = config.default_card_hoa_id; }
        else if (paymentMode === 'credit') { amountReceived = 0; paymentModeHoaId = null; }

        if (paymentMode !== 'credit' && !paymentModeHoaId) return Swal.fire('Error', `HOA for ${paymentMode.toUpperCase()} is not configured.`, 'error');

        const itemsPayload = cart.map(item => ({
            product_id: item.product_id, sale_uom_id: item.uom_id,
            sale_quantity: item.qty, sale_price: item.base_price, 
            conversion_factor: item.conversion_factor || 1,
            base_quantity_sold: (item.qty * (item.conversion_factor || 1)),
            rate_id: item.rate_id,
            adjustments: item.adjustments || [] 
        }));

        const payload = {
            party_id: selectedParty.value, invoice_date: invoiceDate,
            items: itemsPayload, adjustments: [],
            payment: { amount_received: amountReceived, payment_mode_hoa_id: paymentModeHoaId }
        };

        setLoading(true);
        const apiCall = isEditMode ? (cb) => updateSalesInvoice(invoiceIdParam, payload, cb) : (cb) => createSalesInvoice(payload, cb);

        apiCall((response) => {
            setLoading(false);
            
            // Strict success check
            const isSuccess = response && (response.sales_invoice || response.id || response.success === true) && !response.error && !response.name;

            if (isSuccess) {
                const invId = response.sales_invoice?.id || response.id;
                setLastInvoiceId(invId);
                setShowCheckout(false);
                if (isEditMode) {
                    Swal.fire('Success', 'Invoice Updated Successfully!', 'success').then(() => navigate('/franchisee_user/sales/sales-invoices'));
                } else {
                    setShowSuccessModal(true); 
                    setCart([]); 
                    if (defaultParty) setSelectedParty(defaultParty);
                }
            } else {
                // --- INFALLIBLE ERROR EXTRACTOR ---
                let errorMessage = "Failed to process invoice.";

                console.log('KT',response,typeof response);
                

                if (response) {
                    if (typeof response === 'string') {
                        errorMessage = response;
                    }
                    else if (response?.error?.response?.data?.message) {
                        errorMessage = response.error.response.data.message
                    }
                     else if (response.message && !response.message.includes("Request failed with status code")) {
                        errorMessage = response.message;
                    } else if (response.data?.message) {
                        errorMessage = response.data.message;
                    } else if (response.response?.data?.message) {
                        errorMessage = response.response.data.message;
                    } else {
                        // Fallback: Deep JSON search to extract stock or validation errors hidden inside Axios objects
                        try {
                            const jsonStr = JSON.stringify(response);
                            if (jsonStr.includes("Stock Error")) {
                                const match = jsonStr.match(/Stock Error[^"]+/);
                                if (match) errorMessage = match[0].replace(/\\/g, '');
                            } else if (jsonStr.includes("message")) {
                                // Extract any general message field found in the JSON blob
                                const parsed = JSON.parse(jsonStr);
                                if (parsed.message) errorMessage = parsed.message;
                            }
                        } catch (e) {
                            // ignore parsing errors
                        }
                    }
                }

                setShowCheckout(false);
                Swal.fire({
                    icon: 'error',
                    title: 'Unable to Process Invoice',
                    text: errorMessage,
                    confirmButtonColor: '#d33'
                });
            }
        });
    };

    return (
        <div className="container-fluid mt-3">
            <AppToaster showToaster={showToaster} setShowToaster={setShowToaster} toaster={toaster} />
            <AppBreadCrumb title={isEditMode ? 'Edit Sales Invoice' : (isViewMode ? 'View Invoice' : 'Point of Sale')}>
                <li><a href="#" className="f-s-14 f-w-500">Sales</a></li>
                <li className="active"><a href="#" className="f-s-14 f-w-500">{isEditMode ? 'Edit' : (isViewMode ? 'View' : 'Create')}</a></li>
            </AppBreadCrumb>

            {loading ? (
                <div className="d-flex justify-content-center mt-5"><div className="spinner-border text-primary" /></div>
            ) : (
                <div className="row">
                    {!isViewMode && (
                        <div className="col-md-6 col-lg-7">
                            <ProductGrid products={products} searchTerm={searchTerm} setSearchTerm={setSearchTerm} searchInputRef={searchInputRef} addToCart={addToCart} />
                        </div>
                    )}

                    <div className={isViewMode ? "col-md-12" : "col-md-6 col-lg-5"}>
                        <div className="card shadow-sm">
                            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
                                <h5 className="mb-0 text-white">Invoice Details</h5>
                                {isEditMode && <span className="badge bg-warning text-dark">EDIT MODE</span>}
                            </div>
                            <div className="card-body p-3">
                                <div className="mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label fw-bold mb-0">Customer</label>
                                        {!isReadOnly && <button className="btn btn-link btn-sm p-0" type="button" onClick={() => setShowPartyModal(true)}>+ Add New</button>}
                                    </div>
                                    <Select options={parties.map(p => ({ value: p.id, label: p.name }))} value={selectedParty} onChange={setSelectedParty} isDisabled={isReadOnly} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Date</label>
                                    <input type="date" className="form-control" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} disabled={isReadOnly} />
                                </div>

                                <div className="cart-items-container" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
                                    <CartTable 
                                        cart={cart} 
                                        handleQtyChange={handleQtyChange} 
                                        handleUomChange={handleUomChange} 
                                        handleRateChange={handleRateChange} 
                                        removeFromCart={removeFromCart} 
                                        isReadOnly={isReadOnly} 
                                        calculateItemFinancials={calculateItemFinancials} 
                                    />
                                </div>
                            </div>
                           <div className="card-footer bg-light">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="mb-0 text-muted">Grand Total</h5>
                                    <h3 className="mb-0 text-success fw-bold">₹{grandTotal.toFixed(2)}</h3>
                                </div>
                                
                                {/* Checkout / Update Button */}
                                {!isReadOnly && (
                                    <button 
                                        className={`btn btn-lg btn-success w-100 ${isEditMode ? 'mb-2' : ''}`} 
                                        type="button" 
                                        onClick={() => setShowCheckout(true)}
                                    >
                                        <i className="ti ti-check me-2"></i> {isEditMode ? 'Update Invoice' : 'Proceed to Checkout'}
                                    </button>
                                )}

                                {/* Print Button (Only visible in Edit or View modes) */}
                                {(isEditMode || isViewMode) && (
                                    <button 
                                        className="btn btn-lg btn-outline-primary w-100" 
                                        type="button" 
                                        onClick={() => {
                                            const payload = {
                                                selectedOption: "invoice-print",
                                                selectedType: "detail",
                                                reportFormat: "pdf",
                                                sales_invoice_id: invoiceIdParam, // Use the ID from the URL
                                                from_date: invoiceDate, 
                                                to_date: invoiceDate
                                            };
                                            printPDF('/api/parameter-report/sales', payload, (res) => {
                                                if (res && res.error) console.error("Print failed:", res.error);
                                            });
                                        }}
                                    >
                                        <i className="ti ti-printer me-2"></i> Print Invoice
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- NEW NATIVE REACT BARCODE QUANTITY MODAL --- */}
            {showBarcodeQtyModal && scannedProduct && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content shadow-lg border-0">
                            <div className="modal-header bg-primary text-white border-0">
                                <h5 className="modal-title"><i className="ti ti-barcode me-2"></i>Item Scanned</h5>
                                <button type="button" className="btn-close btn-close-white" onClick={() => setShowBarcodeQtyModal(false)}></button>
                            </div>
                            <div className="modal-body text-center p-4">
                                <h5 className="text-dark mb-1">{scannedProduct.name}</h5>
                                <p className="text-muted small mb-3">Code: {scannedProduct.code}</p>
                                
                                <label className="form-label fw-bold text-secondary">Enter Quantity</label>
                                <input 
                                    type="number" 
                                    className="form-control form-control-lg text-center mx-auto mb-2" 
                                    style={{ width: '120px', fontSize: '24px', fontWeight: 'bold' }}
                                    value={scannedQty}
                                    onChange={(e) => setScannedQty(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleConfirmBarcodeQty();
                                        }
                                    }}
                                    ref={barcodeQtyInputRef}
                                    min="0.1"
                                    step="any"
                                />
                                <small className="text-muted">Press Enter to Add</small>
                            </div>
                            <div className="modal-footer justify-content-center bg-light border-0">
                                <button className="btn btn-secondary px-4" onClick={() => setShowBarcodeQtyModal(false)}>Cancel</button>
                                <button className="btn btn-primary px-4" onClick={handleConfirmBarcodeQty}>
                                    <i className="ti ti-shopping-cart-plus me-1"></i> Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Check out Modal */}
            {showCheckout && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header"><h5 className="modal-title">Select Payment Method</h5><button type="button" className="btn-close" onClick={() => setShowCheckout(false)}></button></div>
                            <div className="modal-body">
                                <select className="form-select mb-3" value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
                                    <option value="cash">Cash</option><option value="qr">QR Code / UPI</option><option value="card">Card</option><option value="credit">Credit (Pay Later)</option>
                                </select>
                                <div className="p-3 bg-light rounded text-center"><span className="text-muted d-block">Amount Payable</span><h2 className="text-success fw-bold">₹{grandTotal.toFixed(2)}</h2></div>
                            </div>
                            <div className="modal-footer"><button className="btn btn-secondary" onClick={() => setShowCheckout(false)}>Cancel</button><button className="btn btn-success" onClick={submitInvoice}>Confirm</button></div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Quick Customer Modal */}
            {showPartyModal && (
                 <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header"><h5 className="modal-title">New Customer</h5><button type="button" className="btn-close" onClick={() => setShowPartyModal(false)}></button></div>
                            <div className="modal-body">
                                <input type="text" className="form-control mb-2" placeholder="Name" value={newPartyName} onChange={(e) => setNewPartyName(e.target.value)} />
                                <input type="text" className="form-control mb-2" placeholder="Phone" value={newPartyPhone} onChange={(e) => setNewPartyPhone(e.target.value)} />
                                <input type="text" className="form-control" placeholder="GSTIN" value={newPartyGstin} onChange={(e) => setNewPartyGstin(e.target.value)} />
                            </div>
                            <div className="modal-footer"><button className="btn btn-primary" onClick={handleCreateParty}>Save Customer</button></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content text-center">
                            <div className="modal-body p-5">
                                <div className="mb-4 text-success"><i className="ti ti-circle-check f-s-60"></i></div>
                                <h3>Sale Completed!</h3>
                                <p className="text-muted">Invoice #{lastInvoiceId} generated successfully.</p>
                                <div className="d-grid gap-2 col-8 mx-auto mt-4">
                                    <button 
                                        className="btn btn-outline-primary" 
                                        onClick={() => {
                                            const payload = {
                                                selectedOption: "invoice-print",
                                                selectedType: "detail",
                                                reportFormat: "pdf",
                                                sales_invoice_id: lastInvoiceId,
                                                from_date: invoiceDate, 
                                                to_date: invoiceDate
                                            };
                                            printPDF('/api/parameter-report/sales', payload, (res) => {
                                                if (res && res.error) console.error("Print failed:", res.error);
                                            });
                                        }}
                                    >
                                        Print Invoice
                                    </button>
                                    <button className="btn btn-primary mt-2" onClick={() => setShowSuccessModal(false)}>New Sale</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesInvoiceForm;